<template>
    <div class="space-y-3">
        <CFormFileInput
            :model-value="modelValue || undefined"
            accept="application/pdf,.pdf"
            :lang="lang"
            :disabled="busy || disabled"
            required
            @update:model-value="$emit('update:modelValue', $event)"
        />
        <div v-if="signedIn" class="space-y-2 text-sm">
            <div
                v-if="savedCv"
                class="rounded-lg border border-gray-200 bg-white p-3 dark:border-default dark:bg-surface-card"
            >
                <p class="font-medium">
                    {{ text.saved }}: <span class="break-all">{{ savedCv.name }}</span>
                </p>
                <div class="mt-2 flex flex-wrap gap-3">
                    <CButton
                        variant="primary-link"
                        :aria-label="text.use"
                        :disabled="busy || disabled"
                        @click="useSavedCv"
                        >{{ text.use }}</CButton
                    >
                    <CButton variant="text" :aria-label="text.remove" :disabled="busy || disabled" @click="forgetCv">{{
                        text.remove
                    }}</CButton>
                </div>
            </div>
            <CButton
                v-if="canSave"
                variant="primary-link"
                :aria-label="savedCv ? text.replace : text.save"
                :disabled="busy || disabled"
                @click="saveCv"
            >
                {{ savedCv ? text.replace : text.save }}
            </CButton>
            <p class="text-xs text-gray-500 dark:text-txt-body">{{ text.privacy }}</p>
        </div>
        <p v-if="busy" role="status" class="text-sm text-gray-600 dark:text-txt-body">{{ text.loading }}</p>
        <p v-if="message" role="status" class="text-sm text-green-700 dark:text-green-300">{{ message }}</p>
        <p v-if="error" role="alert" class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
    </div>
</template>

<script setup lang="ts">
import { deleteObject, getBlob, ref as storageRef, uploadBytes } from 'firebase/storage'
import type { SavedJobCv } from '#shared/types/User'
import { isReusableCvFile, isSavedJobCv, MAX_SAVED_CV_BYTES } from '@/utils/savedJobCv'
import { useAuth } from '@/composables/auth/useAuth'

const props = withDefaults(defineProps<{ modelValue?: File | null; lang?: string; disabled?: boolean }>(), {
    lang: 'sk',
    modelValue: null,
    disabled: false
})
const emit = defineEmits<{ (event: 'update:modelValue', value: File | null): void }>()
const store = useApplicationStore()
const { $firebase } = useNuxtApp()
const { getCurrentUser } = useAuth()
const { track } = useCheese()
const busy = ref(false)
const message = ref('')
const error = ref('')
const selectedSavedPath = ref('')
const signedIn = computed(() => Boolean(store.userId && getCurrentUser()?.uid === store.userId))
const savedCv = computed(() =>
    isSavedJobCv(store.userPrivateProfile?.savedJobCv, store.userId || '')
        ? (store.userPrivateProfile!.savedJobCv as SavedJobCv)
        : undefined
)
const canSave = computed(() => props.modelValue && isReusableCvFile(props.modelValue) && !selectedSavedPath.value)
const text = computed(() =>
    props.lang === 'en'
        ? {
              saved: 'Saved CV',
              use: 'Use this PDF',
              remove: 'Remove saved PDF',
              save: 'Save PDF for future applications',
              replace: 'Replace saved PDF',
              privacy: 'Only you can access the saved PDF. Each application keeps its own copy.',
              loading: 'Working with your PDF…',
              success: 'PDF saved for future applications.',
              forgotten: 'Saved PDF removed. Submitted applications keep their copies.',
              failure: 'Could not access your saved PDF. Try again or choose a file from your device.'
          }
        : {
              saved: 'Uložený životopis',
              use: 'Použiť toto PDF',
              remove: 'Odstrániť uložené PDF',
              save: 'Uložiť PDF na ďalšie žiadosti',
              replace: 'Nahradiť uložené PDF',
              privacy: 'Uložené PDF je prístupné iba tebe. Každá odoslaná žiadosť má vlastnú kópiu.',
              loading: 'Pracujem s tvojím PDF…',
              success: 'PDF je uložené na ďalšie žiadosti.',
              forgotten: 'Uložené PDF je odstránené. Odoslané žiadosti si ponechajú svoje kópie.',
              failure: 'S uloženým PDF sa nepodarilo pracovať. Skús znova alebo vyber súbor zo zariadenia.'
          }
)

function assertOwner(uid: string) {
    if (!uid || store.userId !== uid || getCurrentUser()?.uid !== uid) throw new Error('Account changed')
}

async function run(action: (uid: string) => Promise<void>) {
    if (busy.value || props.disabled || !signedIn.value) return
    const uid = store.userId!
    busy.value = true
    error.value = message.value = ''
    try {
        await action(uid)
    } catch {
        error.value = text.value.failure
    } finally {
        busy.value = false
    }
}

async function removeFile(cv?: SavedJobCv) {
    if (!cv) return
    // A failed cleanup must not undo a successful profile update.
    await deleteObject(storageRef($firebase.storage, cv.path)).catch(() => {})
}

async function saveCv() {
    await run(async (uid) => {
        const file = props.modelValue
        if (!file || !isReusableCvFile(file)) throw new Error('Invalid PDF')
        const previous = savedCv.value
        const path = `talentbase/${uid}/saved-job-cv/${crypto.randomUUID()}.pdf`
        assertOwner(uid)
        await uploadBytes(storageRef($firebase.storage, path), file, { contentType: 'application/pdf' })
        const cv: SavedJobCv = {
            path,
            name: file.name.slice(0, 255),
            size: file.size,
            savedAt: new Date().toISOString()
        }
        try {
            assertOwner(uid)
            await $firebase.updateDocument($firebase.collections.users, uid, { savedJobCv: cv })
        } catch (cause) {
            await removeFile(cv)
            throw cause
        }
        assertOwner(uid)
        if (store.userPrivateProfile) store.userPrivateProfile.savedJobCv = cv
        selectedSavedPath.value = path
        await removeFile(previous)
        message.value = text.value.success
        track('job_cv_saved', { replaced: Boolean(previous) })
    })
}

async function useSavedCv() {
    await run(async (uid) => {
        const cv = savedCv.value
        if (!cv) return
        assertOwner(uid)
        const blob = await getBlob(storageRef($firebase.storage, cv.path), MAX_SAVED_CV_BYTES)
        assertOwner(uid)
        const file = new File([blob], cv.name, { type: blob.type })
        if (!isReusableCvFile(file)) throw new Error('Invalid saved PDF')
        emit('update:modelValue', file)
        await nextTick()
        selectedSavedPath.value = cv.path
        track('job_cv_reused')
    })
}

async function forgetCv() {
    await run(async (uid) => {
        const cv = savedCv.value
        assertOwner(uid)
        await $firebase.updateDocument($firebase.collections.users, uid, { savedJobCv: null })
        assertOwner(uid)
        if (store.userPrivateProfile) store.userPrivateProfile.savedJobCv = null
        await removeFile(cv)
        selectedSavedPath.value = ''
        message.value = text.value.forgotten
    })
}

watch(
    () => props.modelValue,
    () => {
        selectedSavedPath.value = ''
        message.value = ''
    }
)
watch(
    () => store.userId,
    () => {
        selectedSavedPath.value = ''
        message.value = error.value = ''
    }
)
</script>
